<template>
    <div class="widget-filters">
        <v-row dense class="ma-0 align-end">
            <v-col
                v-for="def in definitions"
                :key="def.key"
                cols="12"
                :md="compact ? 12 : (def.type === 'date_range' ? 6 : 3)">

                <!-- Periodo dal / al con scelte rapide -->
                <template v-if="def.type === 'date_range'">
                    <div class="d-flex align-center flex-wrap mb-1">
                        <span class="caption grey--text mr-2">{{ def.label }}</span>
                        <v-btn-toggle
                            :value="quickPeriodFor(def.key)"
                            dense
                            group
                            color="primary"
                            @change="setQuickPeriod(def.key, $event)">
                            <v-btn v-for="p in quickPeriods" :key="p.value" :value="p.value" x-small text>{{ p.text }}</v-btn>
                        </v-btn-toggle>
                    </div>
                    <v-row dense class="ma-0">
                        <v-col cols="6" class="pa-0 pr-1">
                            <gmh-date-picker
                                :value="rangeValue(def.key, 'from')"
                                label="Dal"
                                :max="rangeValue(def.key, 'to') || undefined"
                                hide-details
                                append-icon="mdi-calendar"
                                @input="setRange(def.key, 'from', $event)"
                            ></gmh-date-picker>
                        </v-col>
                        <v-col cols="6" class="pa-0 pl-1">
                            <gmh-date-picker
                                :value="rangeValue(def.key, 'to')"
                                label="Al"
                                :min="rangeValue(def.key, 'from') || undefined"
                                hide-details
                                append-icon="mdi-calendar"
                                @input="setRange(def.key, 'to', $event)"
                            ></gmh-date-picker>
                        </v-col>
                    </v-row>
                </template>

                <!-- Tendina singola o multipla -->
                <v-autocomplete
                    v-else-if="def.type === 'select'"
                    :value="value[def.key]"
                    :items="def.options || []"
                    item-text="text"
                    item-value="value"
                    :label="def.label"
                    :multiple="def.multiple"
                    :small-chips="def.multiple"
                    :deletable-chips="def.multiple"
                    clearable
                    dense
                    outlined
                    hide-details
                    @input="setValue(def.key, $event)"
                ></v-autocomplete>

                <!-- Testo libero -->
                <v-text-field
                    v-else
                    :value="value[def.key]"
                    :label="def.label"
                    clearable
                    dense
                    outlined
                    hide-details
                    @input="setValue(def.key, $event)"
                    @keyup.enter="applyNow"
                ></v-text-field>
            </v-col>

            <v-col cols="12" :md="compact ? 12 : 'auto'" class="d-flex justify-end align-center">
                <v-btn text small color="grey" @click="reset">
                    <v-icon left small>mdi-filter-remove</v-icon>
                    Reimposta
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns'
import { widgetFilterDefaults } from './widgetFilterDefaults'

export default {
    name: 'widget-filters',

    props: {
        // Definizioni risolte dal backend: {key, label, type, multiple, options, default_value}
        definitions: { type: Array, default: () => [] },
        // Valori correnti (v-model): {chiave: valore}
        value: { type: Object, default: () => ({}) },
        // Layout a colonna singola per il tile della dashboard
        compact: { type: Boolean, default: false },
        // Ritardo (ms) tra l'ultima modifica e l'applicazione automatica dei filtri
        applyDelay: { type: Number, default: 500 }
    },

    data() {
        return {
            applyTimer: null,
            quickPeriods: [
                { value: 'today', text: 'Oggi' },
                { value: 'this_week', text: 'Settimana' },
                { value: 'this_month', text: 'Mese' },
                { value: 'last_30_days', text: '30 gg' },
                { value: 'this_year', text: 'Anno' }
            ]
        }
    },

    methods: {
        // Ogni modifica aggiorna il v-model e applica i filtri dopo una breve pausa,
        // cosi' selezionare piu' chip o digitare non lancia una query per ogni tocco
        setValue(key, val) {
            this.$emit('input', { ...this.value, [key]: val })
            this.scheduleApply()
        },

        scheduleApply() {
            clearTimeout(this.applyTimer)
            this.applyTimer = setTimeout(() => this.$emit('apply'), this.applyDelay)
        },

        applyNow() {
            clearTimeout(this.applyTimer)
            this.$emit('apply')
        },

        rangeValue(key, side) {
            const range = this.value[key]
            return range && range[side] ? range[side] : null
        },

        setRange(key, side, val) {
            const range = { ...(this.value[key] || { from: null, to: null }) }
            range[side] = val || null
            this.setValue(key, range)
        },

        // Intervallo {from, to} di una scelta rapida
        presetRange(token) {
            const now = new Date()
            const f = d => format(d, 'yyyy-MM-dd')
            switch (token) {
            case 'today':
                return { from: f(now), to: f(now) }
            case 'this_week':
                return { from: f(startOfWeek(now, { weekStartsOn: 1 })), to: f(endOfWeek(now, { weekStartsOn: 1 })) }
            case 'this_month':
                return { from: f(startOfMonth(now)), to: f(endOfMonth(now)) }
            case 'last_30_days':
                return { from: f(subDays(now, 29)), to: f(now) }
            case 'this_year':
                return { from: f(startOfYear(now)), to: f(endOfYear(now)) }
            default:
                return null
            }
        },

        // Scelta rapida che corrisponde all'intervallo corrente, null se personalizzato
        quickPeriodFor(key) {
            const range = this.value[key]
            if (!range) return null
            const match = this.quickPeriods.find(p => {
                const preset = this.presetRange(p.value)
                return preset.from === range.from && preset.to === range.to
            })
            return match ? match.value : null
        },

        setQuickPeriod(key, token) {
            if (!token) return
            this.setValue(key, this.presetRange(token))
        },

        reset() {
            this.$emit('input', widgetFilterDefaults(this.definitions))
            this.$nextTick(() => this.applyNow())
        }
    },

    beforeDestroy() {
        clearTimeout(this.applyTimer)
    }
}
</script>
